/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { analyticsDescribe } from "../funcs/analyticsDescribe.js";
import { analyticsDimensionValues } from "../funcs/analyticsDimensionValues.js";
import { analyticsQuery } from "../funcs/analyticsQuery.js";
import { analyticsSignalDefinition } from "../funcs/analyticsSignalDefinition.js";
import { analyticsSignalFindings } from "../funcs/analyticsSignalFindings.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AnalyticsDescribeResult } from "../models/components/analyticsdescriberesult.js";
import { AnalyticsDimensionValuesResult } from "../models/components/analyticsdimensionvaluesresult.js";
import { AnalyticsQueryResult } from "../models/components/analyticsqueryresult.js";
import { AnalyticsSignalDefinition } from "../models/components/analyticssignaldefinition.js";
import { AnalyticsSignalFindings } from "../models/components/analyticssignalfindings.js";
import {
  AnalyticsDescribeRequest,
  AnalyticsDescribeSecurity,
} from "../models/operations/analyticsdescribe.js";
import {
  AnalyticsDimensionValuesRequest,
  AnalyticsDimensionValuesSecurity,
} from "../models/operations/analyticsdimensionvalues.js";
import {
  AnalyticsQueryRequest,
  AnalyticsQuerySecurity,
} from "../models/operations/analyticsquery.js";
import {
  AnalyticsSignalDefinitionRequest,
  AnalyticsSignalDefinitionSecurity,
} from "../models/operations/analyticssignaldefinition.js";
import {
  AnalyticsSignalFindingsRequest,
  AnalyticsSignalFindingsSecurity,
} from "../models/operations/analyticssignalfindings.js";
import { unwrapAsync } from "../types/fp.js";

export class Analytics extends ClientSDK {
  /**
   * describe analytics
   *
   * @remarks
   * Describe every dataset and the fields it exposes, with the operators and aggregations each admits.
   */
  async describe(
    request?: AnalyticsDescribeRequest | undefined,
    security?: AnalyticsDescribeSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AnalyticsDescribeResult> {
    return unwrapAsync(analyticsDescribe(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * dimensionValues analytics
   *
   * @remarks
   * List the values a dimension holds inside a window, most frequent first, for filter pickers. Values are resolved after the dataset collapses its observations, so every value returned is one a query would match.
   */
  async dimensionValues(
    request: AnalyticsDimensionValuesRequest,
    security?: AnalyticsDimensionValuesSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AnalyticsDimensionValuesResult> {
    return unwrapAsync(analyticsDimensionValues(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * query analytics
   *
   * @remarks
   * Run a query against one dataset. Grouped: aggregate over dimensions, optionally bucketed by time. Ungrouped: rows at the dataset's grain, newest first.
   */
  async query(
    request: AnalyticsQueryRequest,
    security?: AnalyticsQuerySecurity | undefined,
    options?: RequestOptions,
  ): Promise<AnalyticsQueryResult> {
    return unwrapAsync(analyticsQuery(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * signalDefinition analytics
   *
   * @remarks
   * Inspect the immutable semantic definition used by a signal reading. Requires signals_intelligence and project:read.
   */
  async signalDefinition(
    request: AnalyticsSignalDefinitionRequest,
    security?: AnalyticsSignalDefinitionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AnalyticsSignalDefinition> {
    return unwrapAsync(analyticsSignalDefinition(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * signalFindings analytics
   *
   * @remarks
   * Find cross-session signal patterns, joint-answer co-occurrence and guarded previous-window changes. Aggregate reads require project:read and Signals Intelligence; each evidence reference additionally passes the underlying conversation access checks.
   */
  async signalFindings(
    request: AnalyticsSignalFindingsRequest,
    security?: AnalyticsSignalFindingsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<AnalyticsSignalFindings> {
    return unwrapAsync(analyticsSignalFindings(
      this,
      request,
      security,
      options,
    ));
  }
}
